import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, erpApi } from '../../../api';
import { newIdempotencyKey } from '../../../idempotency';
import { refusalText } from './matchingCopy';

/**
 * The matches a person made in this sitting, and what became of them (CO-170).
 * Each is a request to the store; the store's own answer decides whether it
 * was saved, so a match shows "Saving" until then, never "Saved" on hope. Undo
 * asks the store, with a real event, to put back what the match replaced, and
 * is shown the same way.
 */
export type RecentPhase = 'saving' | 'saved' | 'failed' | 'undoing' | 'undone' | 'undo_failed';

export interface RecentMatch {
  key: string;
  sku: string;
  /** The product, for the person: its name, or its SKU. */
  productLabel: string;
  /** What it was matched to, for the person: the article's code and name. */
  articleLabel: string;
  /** The requests the store was asked for; they are what an undo reverses. */
  requestIds: string[];
  /** The reversal's own requests, once it was asked. */
  undoIds: string[];
  phase: RecentPhase;
  /** Why it failed or could not be undone, in words. */
  message?: string;
}

/** What a match needs to be remembered: who it is about and the requests that carry it. */
export type SavedMatch = Pick<RecentMatch, 'sku' | 'productLabel' | 'articleLabel' | 'requestIds'>;

const POLL_MS = 3000;
const KEEP = 20;

export const undoablePhases: RecentPhase[] = ['saving', 'saved'];

export function useRecentMatches(connectionId: string, onSettled: () => void) {
  const [items, setItems] = useState<RecentMatch[]>([]);
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const settled = useRef(onSettled);
  settled.current = onSettled;

  const patch = useCallback((key: string, change: Partial<RecentMatch>) => {
    setItems((current) => current.map((item) => (item.key === key ? { ...item, ...change } : item)));
  }, []);

  const add = useCallback((match: SavedMatch) => {
    const item: RecentMatch = { ...match, key: newIdempotencyKey(), undoIds: [], phase: 'saving' };
    setItems((current) => [item, ...current].slice(0, KEEP));
    return item.key;
  }, []);

  const undo = useCallback(
    async (key: string) => {
      const item = itemsRef.current.find((entry) => entry.key === key);
      if (!item || !undoablePhases.includes(item.phase)) return;
      patch(key, { phase: 'undoing', message: undefined });
      try {
        const result = await erpApi.undoMatches(connectionId, item.requestIds, newIdempotencyKey());
        if (result.requested === 0) {
          const why = result.rows.find((row) => row.outcome === 'skipped');
          // Nothing to put back: the match was never saved, or its code changed since.
          patch(key, {
            phase: why?.reason === 'nothing_saved' ? 'failed' : 'undo_failed',
            message: why?.message ?? 'Nothing was undone.',
          });
          settled.current();
          return;
        }
        patch(key, { phase: 'undoing', undoIds: result.pending_ids });
        settled.current();
      } catch (cause) {
        patch(key, { phase: 'undo_failed', message: cause instanceof ApiError || cause instanceof Error ? cause.message : 'The undo could not be sent.' });
      }
    },
    [connectionId, patch],
  );

  // The store answers within a minute or so: read the open requests until it has.
  const open = items.some((item) => item.phase === 'saving' || (item.phase === 'undoing' && item.undoIds.length > 0));
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    const tick = async () => {
      const waiting = itemsRef.current.filter((item) => item.phase === 'saving' || (item.phase === 'undoing' && item.undoIds.length > 0));
      let changed = false;
      for (const item of waiting) {
        try {
          const ids = item.phase === 'saving' ? item.requestIds : item.undoIds;
          const progress = await erpApi.matchProgress(connectionId, ids);
          if (cancelled || !progress.done) continue;
          const failure = progress.failures[0];
          if (item.phase === 'saving') {
            patch(item.key, failure ? { phase: 'failed', message: refusalText(failure.reason) } : { phase: 'saved' });
          } else {
            patch(item.key, failure ? { phase: 'undo_failed', message: refusalText(failure.reason) } : { phase: 'undone' });
          }
          changed = true;
        } catch {
          // A read that failed is tried again on the next tick.
        }
      }
      if (changed && !cancelled) settled.current();
    };
    const handle = window.setInterval(() => void tick(), POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(handle);
    };
  }, [open, connectionId, patch]);

  return { items, add, undo };
}
